import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Spinner } from '@ixicare/ui';
import { Send, TriangleAlert } from 'lucide-react';
import { DeviceType, ProfileType, Site } from '@ixicare/resources';
import connect from '../../../store/connect';
import DevicesStore from '~modules/devices/store/Devices';
import AuthStore from '~store/Auth';
import PermissionsStore from '~store/Permissions';
import NotificationsStore from '~store/Notifications';
import { UserAction } from '~config/toastNotification';
import { t } from '~i18n';
import { failureMessage } from '~utils/error';
import { useSelectedSite } from '~/hooks/useSelectedSite';
import { FieldValues, Role } from '../components/DeviceConfigForm/schema';
import { GatingContext } from '../components/DeviceConfigForm/utils/gating';
import { State } from '~store/types';
import DeviceConfigForm from '../components/DeviceConfigForm';
import SendConfigToDevicesModal from '../components/SendConfigToDevices';
import { deviceTypeLabels } from '../utils/deviceLabels';

interface Props {
  devicesStore: DevicesStore;
  authStore: AuthStore;
  permissions: PermissionsStore;
  notifications: NotificationsStore;
}

const profileTypeToRole = (pt: ProfileType | null | undefined): Role => {
  switch (pt) {
    case ProfileType.WatcherrEmployee:
      return 'WATCHERR_EMPLOYEE';
    case ProfileType.Installer:
      return 'INSTALLER';
    default:
      return 'INSTALLER';
  }
};

const DeviceSiteConfigContainer: React.FC<Props> = ({ devicesStore, authStore, permissions, notifications }) => {
  const [searchParams] = useSearchParams();
  const [selectedSite] = useSelectedSite();

  // The exact device types this form writes, decided by the tab it was opened from — the
  // configuration service stores strictly per type and ignores keys it does not know.
  const types = React.useMemo(
    () => (searchParams.get('types') ?? '').split(',').filter(Boolean) as DeviceType[],
    [searchParams]
  );

  const [values, setValues] = React.useState<FieldValues | null>(null);
  // The site's own layer is the only one it has: there is nothing between it and the type defaults.
  const configLayers = React.useMemo(() => ({ site: values ?? undefined }), [values]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [sendDialogOpen, setSendDialogOpen] = React.useState(false);
  // What the last action means for the devices: saved and not yet sent, or on its way. Held here
  // because only this container knows which of the two happened; the form drops it as soon as the
  // values on screen stop matching it.
  const [notice, setNotice] = React.useState<React.ReactNode>(null);

  const siteUuid = selectedSite?.uuid;

  React.useEffect(() => {
    if (!siteUuid || types.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    Site.getSiteDeviceSettings(siteUuid)
      .then((config) => {
        if (cancelled) return;
        // Every type in the group shares one form, so any of them describes the whole group.
        // A site the configuration service has never seen has no layer at all, which is "all
        // defaults" rather than an error.
        setValues(config[types[0]] ?? {});
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteUuid, types]);

  // No firmware version: a site spans versions, so the form shows the base defaults and an
  // ungated superset of fields, as the bulk form does for the same reason.
  React.useEffect(() => {
    if (!authStore.userType || types.length === 0) return;
    devicesStore.reset();
    devicesStore.loadConfigSchema(types[0]);
  }, [authStore.userType, types, devicesStore]);

  if (types.length === 0) {
    return <Alert type="error">{t('device-config.error.load', { message: 'No device type selected.' })}</Alert>;
  }

  if (!selectedSite) {
    return <Alert type="error">{t('device-config.error.load', { message: 'No site selected.' })}</Alert>;
  }

  if (devicesStore.schemaError || error) {
    return (
      <Alert type="error">{t('device-config.error.load', { message: 'Could not load device configuration.' })}</Alert>
    );
  }

  if (devicesStore.schemaLoading || !devicesStore.schema || loading || values === null) {
    return <Spinner />;
  }

  const labels = deviceTypeLabels(types);

  const gating: GatingContext = {
    role: profileTypeToRole(authStore.userType),
    permissions: permissions.names,
  };
  // The one gate that is really enforced here: PATCH /sites/<id>/settings checks it, unlike the
  // device write, which only checks access to the device.
  const canEditSiteDefaults = permissions.has('can_edit_site_configuration');

  // The site permission covers the push itself; clearing every device's own settings is a wider
  // blow, so the backend asks for the devices permission on top and the option is only offered
  // with it.
  const canClearDeviceSettings = permissions.has('can_edit_all_devices');

  const onSendToDevices = async (clearDeviceSettings: boolean) => {
    try {
      // The types this form writes, and only those: the site layer is stored per device type, so
      // pushing the tab that was just saved must not reconfigure the rest of the site.
      const job = await Site.createSiteDeviceSettingsOtaJob(selectedSite.uuid, clearDeviceSettings, types);
      // An identical push already in flight was folded into it, so nothing was scheduled and
      // there is no count to report -- saying "sent" would claim more than happened.
      if (job.status === 'already_running') {
        notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'warning',
          message: t('site-device-config.send.alreadyRunning'),
        });
        return;
      }
      notifications.addUserActionNotification({
        type: UserAction.DeviceConfigUpdate,
        notificationType: 'success',
        message: t('site-device-config.send.success', { count: job.devicesTargeted ?? 0 }),
      });
      setNotice(
        <>
          <Send size="1.15em" strokeWidth={2} />
          {t('site-device-config.send.onItsWay', { count: job.devicesTargeted ?? 0 })}
        </>
      );
    } catch (e) {
      notifications.addUserActionNotification({
        type: UserAction.DeviceConfigUpdate,
        notificationType: 'error',
        message: failureMessage(e, t('site-device-config.send.failure')),
      });
      // Keeps the dialog open so the choice made in it is not lost.
      throw e;
    }
  };

  const onSubmit = async (payload: FieldValues) => {
    // The same values for every type in the group: they share a form, and leaving one out would
    // let devices of that type keep the old values with nothing on screen saying so.
    const body = Object.fromEntries(types.map((type) => [type, payload]));
    try {
      const updated = await Site.patchSiteDeviceSettings(selectedSite.uuid, body);
      setValues((current) => {
        const fromResponse = updated?.[types[0]];
        if (fromResponse && typeof fromResponse === 'object') return fromResponse;
        const next = { ...current };
        for (const [key, value] of Object.entries(payload)) {
          if (value === null) delete next[key];
          else next[key] = value;
        }
        return next;
      });
    } catch (e) {
      notifications.addUserActionNotification({
        type: UserAction.DeviceConfigUpdate,
        notificationType: 'error',
        message: failureMessage(e, t('alerts.device.config.update.failure')),
      });
      // Keep the form dirty so the user can retry.
      throw new Error('Site settings update failed');
    }
    notifications.addUserActionNotification({
      type: UserAction.DeviceConfigUpdate,
      notificationType: 'success',
      message: t('alerts.device.config.update.success'),
    });
    // Deliberately stays on the page, unlike the device forms: the notice below is the only thing
    // telling the user their change has not reached any device yet.
    setNotice(
      <>
        <TriangleAlert size="1.15em" strokeWidth={2} />
        {t('site-device-config.notSentToDevices')}
      </>
    );
  };

  return (
    <>
      <DeviceConfigForm
        schema={devicesStore.schema}
        title={t('device-config.crumbs.siteConfig')}
        subtitle={t('device-config.page.siteSubtitle')}
        configValues={values}
        ownLayer="site"
        configLayers={configLayers}
        scope={{
          kind: 'site',
          label: `${selectedSite.name} — ${labels.join(', ')}`,
          href: `/sites/${selectedSite.uuid}`,
          deviceTypeLabels: labels,
          exportName: `site-${selectedSite.uuid}-${types[0].toLowerCase()}`,
        }}
        isInstaller={authStore.userType === ProfileType.Installer}
        gating={gating}
        readOnly={!canEditSiteDefaults}
        onSubmit={onSubmit}
        notice={notice}
        onSendToDevices={() => setSendDialogOpen(true)}
      />
      {sendDialogOpen && (
        <SendConfigToDevicesModal
          deviceTypeLabels={labels}
          canClearDeviceSettings={canClearDeviceSettings}
          onClose={() => setSendDialogOpen(false)}
          onConfirm={onSendToDevices}
        />
      )}
    </>
  );
};

export default connect((state: State) => ({
  devicesStore: state.devices,
  authStore: state.auth,
  permissions: state.permissions,
  notifications: state.notifications,
}))(DeviceSiteConfigContainer);
